/**
 * Passkeys in the browser (API: apps/app/src/routes/passkeys.ts). The
 * WebAuthn ceremony itself is `@simplewebauthn/browser`; in mock mode
 * (`VITE_MOCK=1`) there is no authenticator behind the page, so a stand-in
 * answer is sent instead and the mock accepts it.
 *
 * A passkey only replaces the password: after `signInWithPasskey()` the
 * login page shows the usual TOTP step.
 */
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
  type AuthenticationResponseJSON,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
  type RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import { apiFetch, MOCK_MODE } from './api';
import type { PasskeySummary } from '../types/api';

/** The browser has no WebAuthn at all (old browser, insecure origin). */
export class PasskeysUnsupportedError extends Error {
  constructor() {
    super('This browser does not support passkeys.');
    this.name = 'PasskeysUnsupportedError';
  }
}

/** True when the person closed or cancelled the browser's passkey prompt. */
export function isPasskeyCancelled(err: unknown): boolean {
  return err instanceof Error && (err.name === 'NotAllowedError' || err.name === 'AbortError');
}

function mockId(): string {
  return `mock${Math.random().toString(36).slice(2, 10)}`;
}

async function getAssertion(
  options: PublicKeyCredentialRequestOptionsJSON,
): Promise<AuthenticationResponseJSON> {
  if (MOCK_MODE) {
    const id = mockId();
    return {
      id,
      rawId: id,
      type: 'public-key',
      clientExtensionResults: {},
      response: { clientDataJSON: 'mock', authenticatorData: 'mock', signature: 'mock' },
    };
  }
  if (!browserSupportsWebAuthn()) throw new PasskeysUnsupportedError();
  return startAuthentication({ optionsJSON: options });
}

async function createCredential(
  options: PublicKeyCredentialCreationOptionsJSON,
): Promise<RegistrationResponseJSON> {
  if (MOCK_MODE) {
    const id = mockId();
    return {
      id,
      rawId: id,
      type: 'public-key',
      clientExtensionResults: {},
      response: { clientDataJSON: 'mock', attestationObject: 'mock', transports: ['internal'] },
    };
  }
  if (!browserSupportsWebAuthn()) throw new PasskeysUnsupportedError();
  return startRegistration({ optionsJSON: options });
}

/** Whether the API has passkeys on. Never throws: any failure means "no". */
export async function fetchPasskeysAvailable(): Promise<boolean> {
  try {
    const res = await apiFetch<{ enabled: boolean }>('/auth/passkeys/available');
    return res.enabled === true;
  } catch {
    return false;
  }
}

/** Passkey instead of the password. Resolves when the TOTP step is next. */
export async function signInWithPasskey(): Promise<'totp_required'> {
  const { options } = await apiFetch<{ options: PublicKeyCredentialRequestOptionsJSON }>(
    '/auth/passkeys/login/options',
    { method: 'POST', body: {} },
  );
  const response = await getAssertion(options);
  await apiFetch<{ status: 'totp_required' }>('/auth/passkeys/login/verify', {
    method: 'POST',
    body: { response },
  });
  return 'totp_required';
}

export type StepUpFields = { totpCode: string } | { recoveryCode: string } | Record<never, never>;

/** Step-up, the browser's "create a passkey" prompt, then storing it. */
export async function addPasskey(input: {
  name: string;
  stepUp: StepUpFields;
}): Promise<PasskeySummary> {
  const { options } = await apiFetch<{ options: PublicKeyCredentialCreationOptionsJSON }>(
    '/auth/passkeys/register/options',
    { method: 'POST', body: input.stepUp },
  );
  const response = await createCredential(options);
  // The step-up window opened by the call above covers this one.
  const { passkey } = await apiFetch<{ passkey: PasskeySummary }>(
    '/auth/passkeys/register/verify',
    { method: 'POST', body: { name: input.name, response } },
  );
  return passkey;
}
